import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  OnDestroy,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { UserAuthenticationService } from '../../../../../administration/src/lib/services/user-authentication.service';
import { LocalStorageManagerService } from '../../services/local-storage-manager.service';
import { environment } from '../../../../../../apps/cinari-erp-client/src/environments/environment';

type ChatbotAttachment = {
  name: string;
  base64: string;
};

type ChatbotTabularOutput = {
  headers: string[];
  columns: string[][];
};

type ChatbotDocumentTemplateRow = {
  key: number;
  name: string;
  url?: string;
};

type ChatbotDocumentViewer = {
  name: string;
  url: string;
  kind: 'pdf' | 'docx';
};

type ChatbotSummaryRow = {
  label: string;
  value: string;
};

type ChatbotAttributeKey = 'location' | 'attribute1' | 'attribute2';

type ChatbotAttributeArrays = Record<ChatbotAttributeKey, string[]>;

type ChatbotAttributeSelection = Record<ChatbotAttributeKey, string | null>;

type ChatbotMessage = {
  sender: 'Client' | 'Agent Amy';
  message: string | Record<string, unknown>;
  type?:
    | 'string'
    | 'StringSummary'
    | 'SuccessString'
    | 'ErrorString'
    | 'TabularData'
    | 'DocumentTabularDisplay'
    | 'AttributeArrayTable'
    | 'Options'
    | 'Pdf'
    | 'Thinking';
  prompt?: string;
  options?: string[];
  selectionTemplate?: string;
  initializationReplies?: string[];
  initializationOptionsStyle?: boolean;
  selectedOption?: string;
  attachmentName?: string;
  pdfName?: string;
  pdfUrl?: string;
  tabularOutput?: ChatbotTabularOutput;
  tabularPromptTemplate?: string;
  tabularPage?: number;
  documentTemplates?: ChatbotDocumentTemplateRow[];
  documentSelectedKeys?: number[];
  documentSchedulePeRow?: ChatbotDocumentTemplateRow;
  documentSitePlanRow?: ChatbotDocumentTemplateRow;
  attributeArrays?: ChatbotAttributeArrays;
  attributeSelection?: ChatbotAttributeSelection;
  attributePromptTemplate?: string;
  id?: number;
  ts?: string;
};

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnDestroy {
  readonly TABULAR_PAGE_SIZE = 8;
  readonly PERSISTED_PAIR_LIMIT = 15;
  readonly initializationGreetingTitle = "Hi, I'm Amy 👋";
  readonly initializationGreetingBody =
    "Your Treasure Hill Homes real estate assistant. I'll guide you through the APS deal flow where would you like to start?";
  readonly initializationAvatarBotSize = 66;

  #localStorageService = inject(LocalStorageManagerService);
  #authService = inject(UserAuthenticationService);
  #platformId = inject(PLATFORM_ID);
  #router = inject(Router);
  #elementRef = inject(ElementRef);
  #sanitizer = inject(DomSanitizer);
  #chatbotInitializedKey = 'chatbot-initialized';
  #chatbotMessagesKey = 'chatbot-messages';
  #chatbotVisibleKey = 'chatbot-visible';

  isChatbot: boolean = false;
  isChatbotVisible: boolean = true;
  isOnSalesRoute: boolean = this.#router.url.startsWith('/sales');
  isExpanded: boolean = false;
  currentRole: string = this.#authService.getUserRole();
  accessToken: string = this.#authService.getToken();

  value: string = '';
  pendingAttachment: ChatbotAttachment | null = null;
  attachmentError: string = '';
  isInitializingChatbot: boolean = false;

  isRecording: boolean = false;
  recognition: any = null;
  voiceError: string = '';
  liveTranscript: string = '';
  finalTranscript: string = '';
  recSec: number = 0;
  readonly waveBars = [6, 11, 16, 9, 13, 8];

  /* ── Pebble launcher / chrome state ── */
  unread = false;
  toast: { text: string; icon: string } | null = null;

  //draggable feature
  dragPosition: { x: number; y: number } | null = null;
  renderPosition: { x: number; y: number } | null = null;
  #isDragging = false;
  #dragMoved = false;
  #dragStart = { x: 0, y: 0, originX: 0, originY: 0 };

  /* ── Typewriter ── */
  typedCounts: Record<number, number> = {};
  doneIds = new Set<number>();
  pendingThinkingMessageId: number | null = null;

  messages: ChatbotMessage[] = [];

  @ViewChild('bottomRef') bottomRef!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('customDocumentInput') customDocumentInput!: ElementRef<HTMLInputElement>;

  #msgSeq = 0;
  #toastTimer: any = null;
  #recTimer: any = null;
  #secTimer: any = null;
  #typeTimers: Record<number, any> = {};
  #visibilitySyncTimer: any = null;

  @HostBinding('style.display')
  get hostDisplay(): string {
    return this.isChatbotVisible && this.isOnSalesRoute ? '' : 'none';
  }

  @HostBinding('style.left')
  get hostLeft(): string | null {
    return this.renderPosition ? `${this.renderPosition.x}px` : null;
  }
  @HostBinding('style.top')
  get hostTop(): string | null {
    return this.renderPosition ? `${this.renderPosition.y}px` : null;
  }
  @HostBinding('style.right')
  get hostRight(): string | null {
    return this.renderPosition ? 'auto' : null;
  }
  @HostBinding('style.bottom')
  get hostBottom(): string | null {
    return this.renderPosition ? 'auto' : null;
  }

  constructor() {
    this.isChatbotVisible = this.restoreChatbotVisibilityFromStorage();
    this.messages = this.restoreMessagesFromStorage();
    this.initializeVoiceRecognition();
    this.startVisibilitySync();

    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.isOnSalesRoute = this.#router.url.startsWith('/sales');
        if (!this.isOnSalesRoute && this.isChatbot) {
          if (this.isRecording) this.stopVoiceRecording();
          this.isChatbot = false;
        }
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.#toastTimer);
    clearInterval(this.#recTimer);
    clearInterval(this.#secTimer);
    clearInterval(this.#visibilitySyncTimer);
    Object.values(this.#typeTimers).forEach(t => clearInterval(t));
    this.revokeDocumentBlobUrl();
  }

  private startVisibilitySync(): void {
    if (!isPlatformBrowser(this.#platformId)) return;

    clearInterval(this.#visibilitySyncTimer);
    this.#visibilitySyncTimer = setInterval(() => {
      const nextVisible = this.restoreChatbotVisibilityFromStorage();
      if (nextVisible === this.isChatbotVisible) return;

      this.isChatbotVisible = nextVisible;
      if (!nextVisible && this.isChatbot) {
        if (this.isRecording) this.stopVoiceRecording();
        this.isChatbot = false;
      }
    }, 500);
  }

  private initializeVoiceRecognition(): void {
    if (!isPlatformBrowser(this.#platformId)) return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'en-US';
    this.recognition.interimResults = true;
    this.recognition.continuous = true;

    this.recognition.onresult = (event: any) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          this.finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      this.liveTranscript = `${this.finalTranscript}${interimTranscript}`.trim();
      this.value = this.finalTranscript.trim();
    };

    this.recognition.onerror = () => {
      this.voiceError = 'Voice recording failed.';
      this.isRecording = false;
      this.cleanupVoiceTimers();
    };

    this.recognition.onend = () => {
      this.isRecording = false;
      this.value = this.liveTranscript.trim();
      this.cleanupVoiceTimers();
    };
  }

  private restoreChatbotVisibilityFromStorage(): boolean {
    if (!isPlatformBrowser(this.#platformId)) return true;

    const storedVisibility = localStorage.getItem(this.#chatbotVisibleKey);
    if (storedVisibility == null) {
      localStorage.setItem(this.#chatbotVisibleKey, 'true');
      return true;
    }

    return storedVisibility === 'true';
  }

  toggleVoiceRecording(): void {
    if (this.isRecording) {
      this.stopVoiceRecording();
      return;
    }

    this.startVoiceRecording();
  }

  private startVoiceRecording(): void {
    this.voiceError = '';

    if (!this.recognition) {
      this.voiceError = 'Voice recording is not supported in this browser.';
      return;
    }

    this.liveTranscript = '';
    this.finalTranscript = '';
    this.recSec = 0;
    this.isRecording = true;
    this.recognition.start();

    clearInterval(this.#recTimer);
    clearInterval(this.#secTimer);

    this.#recTimer = setInterval(() => {
      const transcript = this.liveTranscript.trim();
      if (!transcript && !this.isRecording) {
        clearInterval(this.#recTimer);
      }
    }, 120);

    this.#secTimer = setInterval(() => {
      this.recSec += 1;
    }, 1000);
  }

  private stopVoiceRecording(): void {
    if (!this.recognition) {
      this.isRecording = false;
      this.cleanupVoiceTimers();
      return;
    }

    this.cleanupVoiceTimers();
    this.recognition.stop();
    this.isRecording = false;
    this.value = this.liveTranscript.trim();
  }

  private cleanupVoiceTimers(): void {
    clearInterval(this.#recTimer);
    clearInterval(this.#secTimer);
    this.#recTimer = null;
    this.#secTimer = null;
  }

  private stopVoiceRecordingAndSend(): void {
    const attachment = this.pendingAttachment;
    this.stopVoiceRecording();

    setTimeout(() => {
      const transcript = (this.liveTranscript || this.finalTranscript || this.value || '').trim();
      if (!transcript && !attachment) return;
      void this.sendMessage(transcript || this.value.trim(), attachment);
    }, 60);
  }

  /* ── Pebble helpers ── */
  private nowTime(): string {
    return new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  fmtSec(sec: number): string {
    return Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
  }

  private tagAgentMessage(msg: ChatbotMessage, animate: boolean): ChatbotMessage {
    const id = ++this.#msgSeq;
    const tagged: ChatbotMessage = { ...msg, id, ts: msg.ts ?? this.nowTime() };
    const animatable = animate && (tagged.type === 'string' || !tagged.type) && typeof tagged.message === 'string';
    if (animatable) {
      const message = tagged.message;
      if (typeof message === 'string') {
        this.startTypewriter(id, message);
      }
    } else {
      this.doneIds.add(id);
    }
    return tagged;
  }

  private startTypewriter(id: number, text: string): void {
    if (!isPlatformBrowser(this.#platformId)) {
      this.doneIds.add(id);
      return;
    }
    const start = Date.now();
    const total = text.length;
    const speed = 13;
    this.typedCounts[id] = 0;
    const timer = setInterval(() => {
      const chars = Math.min(total, Math.round((Date.now() - start) / speed));
      this.typedCounts[id] = chars;
      this.scrollToBottom();
      if (chars >= total) {
        clearInterval(timer);
        delete this.#typeTimers[id];
        this.doneIds.add(id);
      }
    }, speed);
    this.#typeTimers[id] = timer;
  }

  private isAnimating(msg: ChatbotMessage): boolean {
    return msg.id != null && this.typedCounts[msg.id] != null && !this.doneIds.has(msg.id);
  }
  shownMessage(msg: ChatbotMessage): string {
    if (typeof msg.message !== 'string') return '';
    if (this.isAnimating(msg)) return msg.message.slice(0, this.typedCounts[msg.id!] ?? 0);
    return msg.message;
  }
  showCaret(msg: ChatbotMessage): boolean {
    return this.isAnimating(msg);
  }

  getStringSummaryRows(msg: ChatbotMessage): ChatbotSummaryRow[] {
    if (msg.type !== 'StringSummary' || !msg.message || typeof msg.message !== 'object' || Array.isArray(msg.message)) {
      return [];
    }

    return Object.entries(msg.message).map(([label, value]) => ({
      label,
      value: String(value ?? '')
    }));
  }
  getInitializationOptionsMessage(): ChatbotMessage | null {
    return this.messages.find(message => this.isInitializationOptionsMessage(message)) ?? null;
  }
  /* message-run grouping (consecutive same sender) */
  isStartRun(i: number): boolean {
    const prev = this.messages[i - 1];
    return !prev || prev.sender !== this.messages[i].sender;
  }
  isEndRun(i: number): boolean {
    const next = this.messages[i + 1];
    return !next || next.sender !== this.messages[i].sender;
  }
  rowClass(i: number): string {
    const m = this.messages[i];
    const side = m.sender === 'Agent Amy' ? 'agent' : 'client';
    const start = this.isStartRun(i);
    const end = this.isEndRun(i);
    const grp = start && end ? '' : start ? ' grp-start' : end ? ' grp-end' : ' grp-mid';
    return side + grp + (end ? ' grp-end' : '');
  }

  showToast(text: string, icon = 'check'): void {
    this.toast = { text, icon };
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => (this.toast = null), 2400);
  }
  copyValue(v: string): void {
    try {
      if (isPlatformBrowser(this.#platformId) && navigator.clipboard) navigator.clipboard.writeText(v);
    } catch {
      /* clipboard may be blocked */
    }
    this.showToast('Copied to clipboard');
  }
  openPanel(): void {
    if (!this.isChatbot) this.toggleChatbot();
  }

  onLauncherPointerDown(event: PointerEvent): void {
    const host = this.#elementRef.nativeElement as HTMLElement;
    const rect = host.getBoundingClientRect();

    this.#isDragging = true;
    this.#dragMoved = false;
    this.#dragStart = { x: event.clientX, y: event.clientY, originX: rect.left, originY: rect.top };

    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onLauncherPointerMove(event: PointerEvent): void {
    if (!this.#isDragging) return;

    const dx = event.clientX - this.#dragStart.x;
    const dy = event.clientY - this.#dragStart.y;
    if (!this.#dragMoved && Math.hypot(dx, dy) < 4) return;
    this.#dragMoved = true;

    const host = this.#elementRef.nativeElement as HTMLElement;
    const { width, height } = host.getBoundingClientRect();
    const maxX = Math.max(0, window.innerWidth - width);
    const maxY = Math.max(0, window.innerHeight - height);

    this.dragPosition = {
      x: Math.min(Math.max(0, this.#dragStart.originX + dx), maxX),
      y: Math.min(Math.max(0, this.#dragStart.originY + dy), maxY)
    };
    this.renderPosition = this.dragPosition;
  }

  onLauncherPointerUp(): void {
    this.#isDragging = false;
  }

  onLauncherClick(): void {
    if (this.#dragMoved) {
      this.#dragMoved = false;
      return;
    }
    this.openPanel();
  }

  /** Re-derives renderPosition from the untouched drag anchor, clamped to
   * whatever box (launcher or panel) is mounted right now. Never mutates
   * `dragPosition`, so the anchor survives opening/expanding/closing. */
  private reclampDragPosition(): void {
    if (!this.dragPosition || !isPlatformBrowser(this.#platformId)) return;

    setTimeout(() => {
      if (!this.dragPosition) return;
      const host = this.#elementRef.nativeElement as HTMLElement;
      const { width, height } = host.getBoundingClientRect();
      const maxX = Math.max(0, window.innerWidth - width);
      const maxY = Math.max(0, window.innerHeight - height);

      this.renderPosition = {
        x: Math.min(this.dragPosition.x, maxX),
        y: Math.min(this.dragPosition.y, maxY)
      };
    });
  }

  toggleChatbot() {
    this.isChatbot = !this.isChatbot;

    if (this.isChatbot) {
      this.unread = false;
      this.reclampDragPosition();

      if (!this.hasChatbotBeenInitialized() && !this.isInitializingChatbot) {
        this.isInitializingChatbot = true;
        this.loadInitialMessage();
      }
    } else {
      if (this.isRecording) this.stopVoiceRecording();
      if (this.messages.length) this.unread = true;
      this.reclampDragPosition();
    }
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.reclampDragPosition();
  }

  async clearConversation() {
    this.value = '';
    this.attachmentError = '';
    this.clearAttachment();
    this.voiceError = '';
    this.liveTranscript = '';
    this.finalTranscript = '';
    this.recSec = 0;
    if (this.isRecording) {
      this.stopVoiceRecording();
    }
    this.messages = [];
    this.persistMessagesToStorage();

    this.isInitializingChatbot = true;

    try {
      await this.loadInitialMessage();
    } catch (error) {
      console.error('Failed to reinitialize chatbot', error);
      this.isInitializingChatbot = false;
    }
  }

  triggerAttachmentPicker() {
    this.attachmentError = '';
    this.fileInput?.nativeElement.click();
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.attachmentError = '';

    if (!file) {
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    if (!isPdf && !isDocx) {
      this.attachmentError = 'Only PDF or Word (.docx) files are supported.';
      this.clearAttachment();
      return;
    }

    try {
      const base64 = await this.fileToBase64(file);
      this.pendingAttachment = {
        name: file.name,
        base64
      };
    } catch (error) {
      console.error('Failed to read attachment file', error);
      this.attachmentError = 'Unable to read the selected file.';
      this.clearAttachment();
    } finally {
      input.value = '';
    }
  }

  removeAttachment() {
    this.clearAttachment();
    this.attachmentError = '';
  }

  private async postJson(url: string, payload: Record<string, unknown>, retried = false): Promise<any> {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, access_token: this.accessToken })
    });

    if (res.status === 401 && !retried) {
      await firstValueFrom(this.#authService.refreshToken());
      this.accessToken = this.#authService.getToken();
      return this.postJson(url, payload, true);
    }

    return res.json();
  }

  async loadInitialMessage() {
    try {
      const salesOfficeId = this.#localStorageService.getSalesOfficeIdFromStorage();

      const data = await this.postJson(`${environment.baseURLChatbot}chat_initialization`, {
        sales_office_id: salesOfficeId,
        sales_role: this.currentRole
      });
      const [initializationArray, idsForLocalStorage] = Array.isArray(data) ? data : [data, null];
      const [initializationOptions, initializationReplies] = Array.isArray(initializationArray)
        ? initializationArray
        : [[], []];

      this.storeInitializationData(idsForLocalStorage);

      const optionList = Array.isArray(initializationOptions)
        ? initializationOptions.map((option: any) => String(option ?? ''))
        : [];
      const replyList = Array.isArray(initializationReplies)
        ? initializationReplies.map((reply: any) => String(reply ?? ''))
        : [];

      this.messages = [
        this.tagAgentMessage(
          optionList.length === 2
            ? {
                sender: 'Agent Amy',
                message: '',
                type: 'Options',
                prompt: `${this.initializationGreetingTitle}\n${this.initializationGreetingBody}`,
                options: optionList,
                initializationReplies: replyList,
                initializationOptionsStyle: true
              }
            : {
                sender: 'Agent Amy',
                message:
                  typeof initializationArray === 'string' ? initializationArray : String(initializationArray ?? ''),
                type: 'string'
              },
          true
        )
      ];
      this.persistMessagesToStorage();
      this.markChatbotInitialized();

      this.scrollToBottom();
    } catch (err) {
      console.error('Init API error', err);
    } finally {
      this.isInitializingChatbot = false;
    }
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    void this.handleSend();
  }

  autoGrowComposer(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private resetComposerHeight(): void {
    const textarea = this.messageInput?.nativeElement;
    if (textarea) textarea.style.height = 'auto';
  }

  async handleSend() {
    if (this.isRecording) {
      this.stopVoiceRecordingAndSend();
      return;
    }

    const hasText = Boolean(this.value.trim());
    const hasAttachment = Boolean(this.pendingAttachment);

    if (!hasText && !hasAttachment) return;

    const msg = this.value.trim();
    await this.sendMessage(msg, this.pendingAttachment);
  }

  async sendSelectedOption(msg: ChatbotMessage) {
    if (!msg.selectedOption) return;

    if (this.isInitializationOptionsMessage(msg)) {
      this.handleInitializationSelection(msg);
      return;
    }

    const selectedValue = this.formatSelectedOption(msg);
    await this.sendMessage(selectedValue);
  }

  handleInitializationOption(msg: ChatbotMessage, option: string): void {
    if (!option) return;

    const updatedMsg = {
      ...msg,
      selectedOption: option
    };

    this.messages = this.messages.map(message => (message.id === msg.id ? updatedMsg : message));
    this.persistMessagesToStorage();
    void this.sendSelectedOption(updatedMsg);
  }

  private isInitializationOptionsMessage(msg: ChatbotMessage): boolean {
    return (
      msg.type === 'Options' &&
      Array.isArray(msg.options) &&
      msg.options.length === 2 &&
      msg.options[0] === 'Create New Deal' &&
      msg.options[1] === 'Update Existing Deal'
    );
  }

  private handleInitializationSelection(msg: ChatbotMessage): void {
    const selectedIndex = msg.options?.indexOf(msg.selectedOption ?? '') ?? -1;
    if (selectedIndex < 0) return;

    const reply = msg.initializationReplies?.[selectedIndex] ?? '';
    const nextMessage = reply ? reply : String(msg.selectedOption ?? '');

    this.messages = this.messages.map(message =>
      message.id === msg.id
        ? {
            ...message,
            message: nextMessage,
            type: 'string',
            initializationOptionsStyle: false
          }
        : message
    );
    this.persistMessagesToStorage();
    this.scrollToBottom();
  }

  async sendMessage(msg: string, attachment: ChatbotAttachment | null = null) {
    this.messages.push({
      sender: 'Client',
      message: msg,
      attachmentName: attachment?.name,
      id: ++this.#msgSeq,
      ts: this.nowTime()
    });
    this.persistMessagesToStorage();

    this.value = '';
    this.liveTranscript = '';
    this.finalTranscript = '';
    this.recSec = 0;
    this.resetComposerHeight();

    if (attachment) {
      this.clearAttachment();
    }

    this.showThinkingMessage();
    this.scrollToBottom();

    try {
      const idsForLocalStorage = this.getIdsForLocalStorage();
      const isSequentialExecution = /^\s*\/sequential(\s|$)/i.test(msg);
      const isDocxAttachment = Boolean(attachment) && /\.docx$/i.test(attachment!.name);
      const endpoint =
        isSequentialExecution && isDocxAttachment
          ? 'sequential_execution_document'
          : isSequentialExecution
            ? 'sequential_execution'
            : attachment
              ? 'check_aps_info_document'
              : 'check_aps_info';
      const payload =
        endpoint === 'sequential_execution_document'
          ? {
              docx_base64: this.stripBase64Prefix(attachment!.base64),
              docx_file_name: attachment!.name,
              ids_for_local_storage: idsForLocalStorage,
              sales_role: this.currentRole
            }
          : attachment
            ? {
                aps_data_input: msg,
                pdf_base64: attachment?.base64,
                pdf_file_name: attachment?.name,
                ids_for_local_storage: idsForLocalStorage,
                sales_role: this.currentRole
              }
            : {
                aps_data_input: msg,
                ids_for_local_storage: idsForLocalStorage,
                sales_role: this.currentRole
              };

      const data = await this.postJson(`${environment.baseURLChatbot}${endpoint}`, payload);
      const [outputApsList, updatedIdsForLocalStorage] = Array.isArray(data) ? data : [data, null];

      this.storeInitializationData(updatedIdsForLocalStorage);
      const normalizedMessage = this.normalizeResponse(outputApsList);
      this.clearThinkingMessage();

      this.messages.push(
        this.tagAgentMessage(
          {
            sender: 'Agent Amy',
            ...normalizedMessage
          },
          true
        )
      );
      this.persistMessagesToStorage();

      this.scrollToBottom();
    } catch (err) {
      console.error('Chat API error', err);
      this.clearThinkingMessage();
    }
  }

  get canSend(): boolean {
    return Boolean(this.value.trim() || this.pendingAttachment);
  }

  private clearAttachment() {
    this.pendingAttachment = null;

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(typeof reader.result === 'string' ? reader.result : '');
      };
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read file.'));
      reader.readAsDataURL(file);
    });
  }

  isDocxAttachmentName(name: string): boolean {
    return /\.docx$/i.test(name);
  }

  private stripBase64Prefix(base64: string): string {
    const commaIndex = base64.indexOf(',');
    return commaIndex >= 0 ? base64.slice(commaIndex + 1) : base64;
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.bottomRef) {
        this.bottomRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  storeInitializationData(idsForLocalStorage: any): void {
    if (!idsForLocalStorage || !isPlatformBrowser(this.#platformId)) return;

    localStorage.setItem('ids_for_local_storage', JSON.stringify(idsForLocalStorage));
  }

  getIdsForLocalStorage(): any {
    if (!isPlatformBrowser(this.#platformId)) return null;

    const storedIdsForLocalStorage = localStorage.getItem('ids_for_local_storage');
    if (!storedIdsForLocalStorage) return null;

    try {
      return JSON.parse(storedIdsForLocalStorage);
    } catch {
      return null;
    }
  }

  normalizeResponse(data: any): {
    message: string | Record<string, unknown>;
    type?:
      | 'string'
      | 'StringSummary'
      | 'SuccessString'
      | 'ErrorString'
      | 'TabularData'
      | 'DocumentTabularDisplay'
      | 'AttributeArrayTable'
      | 'Options'
      | 'Pdf'
      | 'Thinking';
    prompt?: string;
    options?: string[];
    selectionTemplate?: string;
    pdfName?: string;
    pdfUrl?: string;
    tabularOutput?: ChatbotTabularOutput;
    tabularPromptTemplate?: string;
    documentTemplates?: ChatbotDocumentTemplateRow[];
    documentSelectedKeys?: number[];
    documentSchedulePeRow?: ChatbotDocumentTemplateRow;
    documentSitePlanRow?: ChatbotDocumentTemplateRow;
    attributeArrays?: ChatbotAttributeArrays;
    attributeSelection?: ChatbotAttributeSelection;
    attributePromptTemplate?: string;
  } {
    if (Array.isArray(data)) {
      const [type, value] = data;

      if (type === 'Options' && Array.isArray(value)) {
        const stringValues = value.filter(item => typeof item === 'string') as string[];
        const prompt = stringValues[0];
        const selectionTemplate = stringValues.length > 1 ? stringValues[stringValues.length - 1] : '';
        const options = value.find(item => Array.isArray(item));

        return {
          message: '',
          type: 'Options',
          prompt: typeof prompt === 'string' ? prompt : '',
          options: Array.isArray(options) ? options : [],
          selectionTemplate: selectionTemplate && selectionTemplate !== prompt ? selectionTemplate : undefined
        };
      }

      if (type === 'String') {
        return {
          message: typeof value === 'string' ? value : String(value ?? ''),
          type: 'string'
        };
      }

      if (type === 'StringSummary') {
        return {
          message: value && typeof value === 'object' && !Array.isArray(value) ? value : {},
          type: 'StringSummary'
        };
      }

      if (type === 'SuccessString') {
        return {
          message: typeof value === 'string' ? value : String(value ?? ''),
          type: 'SuccessString'
        };
      }

      if (type === 'ErrorString') {
        return {
          message: typeof value === 'string' ? value : String(value ?? ''),
          type: 'ErrorString'
        };
      }

      if (type === 'TabularData' && value && typeof value === 'object') {
        const tabularValue = Array.isArray(value) ? value[0] : value;
        const promptTemplate = Array.isArray(value) ? String(value[1] ?? '') : '';
        const headers = Array.isArray(tabularValue.headers)
          ? tabularValue.headers.map((header: any) => String(header ?? ''))
          : [];
        const columns = Array.isArray(tabularValue.columns)
          ? tabularValue.columns.map((column: any) =>
              Array.isArray(column) ? column.map((cell: any) => String(cell ?? '')) : []
            )
          : [];

        return {
          message: '',
          type: 'TabularData',
          tabularPromptTemplate: promptTemplate,
          tabularOutput: {
            headers,
            columns
          }
        };
      }

      if (type === 'DocumentTabularDisplay' && Array.isArray(value)) {
        // Newer payloads pair names with view URLs: [names_array, urls_array].
        // Older payloads are just a flat names_array — keep supporting that shape too.
        const isPairedWithUrls = Array.isArray(value[0]);
        const rawNames: any[] = isPairedWithUrls ? value[0] : value;
        const rawUrls: any[] = isPairedWithUrls && Array.isArray(value[1]) ? value[1] : [];

        const names = rawNames.map((name: any) => String(name ?? ''));
        const urls = rawUrls.map((url: any) => String(url ?? ''));
        // A URL of "empty" (sent by the backend) means no document is available for that
        // row, same meaning as a blank/missing entry.
        const resolveUrl = (rawUrl: string | undefined): string | undefined =>
          rawUrl && rawUrl !== 'empty' ? rawUrl : undefined;
        // The last two entries aren't table rows — they're the fixed Schedule PE / Site Plan
        // template names used by the insert actions below the table. They're still viewable
        // when the backend sends a URL for them, same as any other row.
        const templateNames = names.slice(0, Math.max(0, names.length - 2));

        return {
          message: '',
          type: 'DocumentTabularDisplay',
          documentTemplates: templateNames.map((name, index) => ({
            key: index,
            name,
            url: resolveUrl(urls[index])
          })),
          documentSelectedKeys: [],
          documentSchedulePeRow:
            names.length >= 2
              ? { key: names.length - 2, name: names[names.length - 2], url: resolveUrl(urls[names.length - 2]) }
              : undefined,
          documentSitePlanRow:
            names.length >= 1
              ? { key: names.length - 1, name: names[names.length - 1], url: resolveUrl(urls[names.length - 1]) }
              : undefined
        };
      }

      if (type === 'AttributeArrayTable' && Array.isArray(value)) {
        // Backend order is [subcategory_1_array, subcategory_2_array, location_array, prompt_template].
        const toList = (list: any): string[] =>
          Array.isArray(list) ? list.map((item: any) => String(item ?? '')).filter(Boolean) : [];

        return {
          message: '',
          type: 'AttributeArrayTable',
          attributeArrays: {
            location: toList(value[2]),
            attribute1: toList(value[0]),
            attribute2: toList(value[1])
          },
          attributeSelection: this.emptyAttributeSelection(),
          attributePromptTemplate: String(value[3] ?? '')
        };
      }

      if (type === 'Pdf' && Array.isArray(value)) {
        const [pdfName, pdfUrl, pdfMessage] = value;

        return {
          message: typeof pdfMessage === 'string' ? pdfMessage : String(pdfMessage ?? ''),
          type: 'Pdf',
          pdfName: typeof pdfName === 'string' ? pdfName : '',
          pdfUrl: typeof pdfUrl === 'string' ? pdfUrl : ''
        };
      }
    }

    return {
      message: typeof data === 'string' ? data : String(data ?? ''),
      type: 'string'
    };
  }

  private formatSelectedOption(msg: ChatbotMessage): string {
    const selectedOption = String(msg.selectedOption ?? '');
    if (!selectedOption) return '';

    const template = String(msg.selectionTemplate ?? '').trim();
    if (!template) return selectedOption;

    return template.includes('{}') ? template.replace(/\{\}/g, selectedOption) : `${template} ${selectedOption}`.trim();
  }

  private showThinkingMessage(): void {
    this.clearThinkingMessage();

    const id = ++this.#msgSeq;
    this.pendingThinkingMessageId = id;
    this.messages.push({
      sender: 'Agent Amy',
      message: 'Thinking...',
      type: 'Thinking',
      id,
      ts: this.nowTime()
    });
  }

  private clearThinkingMessage(): void {
    if (!this.pendingThinkingMessageId) return;

    this.messages = this.messages.filter(message => message.id !== this.pendingThinkingMessageId);
    this.pendingThinkingMessageId = null;
    this.persistMessagesToStorage();
  }

  getTabularRows(tabularOutput: ChatbotTabularOutput): string[][] {
    const columns = Array.isArray(tabularOutput.columns) ? tabularOutput.columns : [];
    const maxRows = columns.reduce((max, column) => Math.max(max, column.length), 0);

    return Array.from({ length: maxRows }, (_, rowIndex) => columns.map(column => column[rowIndex] ?? ''));
  }

  getTabularPageCount(tabularOutput: ChatbotTabularOutput): number {
    const rows = this.getTabularRows(tabularOutput);
    return Math.max(1, Math.ceil(rows.length / this.TABULAR_PAGE_SIZE));
  }

  getTabularPageRows(msg: ChatbotMessage): string[][] {
    if (!msg.tabularOutput) return [];

    const rows = this.getTabularRows(msg.tabularOutput);
    const page = Math.min(Math.max(msg.tabularPage ?? 0, 0), this.getTabularPageCount(msg.tabularOutput) - 1);
    const start = page * this.TABULAR_PAGE_SIZE;

    return rows.slice(start, start + this.TABULAR_PAGE_SIZE);
  }

  getTabularRowHeaders(msg: ChatbotMessage): string[] {
    return msg.tabularOutput?.headers ?? [];
  }

  getTabularRowSelectionText(msg: ChatbotMessage, row: string[]): string {
    const template = String(msg.tabularPromptTemplate ?? '').trim();
    if (!template) return '';

    const headers = this.getTabularRowHeaders(msg);
    return template.replace(/\{([^}]+)\}/g, (_, token: string) => {
      const columnIndex = headers.indexOf(token);
      if (columnIndex < 0) {
        return `{${token}}`;
      }

      const cellValue = String(row[columnIndex] ?? '');
      return cellValue === '' ? '""' : cellValue;
    });
  }

  selectTabularRow(msg: ChatbotMessage, row: string[]): void {
    this.value = this.getTabularRowSelectionText(msg, row);
    this.resetComposerHeight();

    setTimeout(() => {
      const textarea = this.messageInput?.nativeElement;
      if (textarea) {
        this.autoGrowComposer(textarea);
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    });
  }

  setTabularPage(msg: ChatbotMessage, page: number): void {
    if (!msg.id || !msg.tabularOutput) return;

    const nextPage = Math.max(0, Math.min(page, this.getTabularPageCount(msg.tabularOutput) - 1));
    this.messages = this.messages.map(message =>
      message.id === msg.id ? { ...message, tabularPage: nextPage } : message
    );
    this.persistMessagesToStorage();
  }

  /* ── Document templates table (DocumentTabularDisplay) ── */
  documentDragMessageId: number | null = null;
  documentDragKeys: number[] = [];
  documentDropIndex: number | null = null;
  // Which message the custom document picker was opened from — the picker's hidden
  // <input> is shared across all DocumentTabularDisplay messages, so this is set right
  // before the click and consumed once the file is chosen.
  customDocumentTargetMessageId: number | null = null;
  // Rows inserted via check_aps_info_document get a fresh negative key so they never
  // collide with the backend's positive, position-based keys (table rows are 0..N-3,
  // the reserved Schedule PE/Site Plan rows are N-2/N-1).
  #customDocumentKeySeq = -1;

  getDocumentTemplates(msg: ChatbotMessage): ChatbotDocumentTemplateRow[] {
    return msg.documentTemplates ?? [];
  }

  isDocumentTemplateSelected(msg: ChatbotMessage, row: ChatbotDocumentTemplateRow): boolean {
    return (msg.documentSelectedKeys ?? []).includes(row.key);
  }

  areAllDocumentTemplatesSelected(msg: ChatbotMessage): boolean {
    const rows = this.getDocumentTemplates(msg);
    return rows.length > 0 && (msg.documentSelectedKeys ?? []).length === rows.length;
  }

  toggleDocumentTemplate(msg: ChatbotMessage, row: ChatbotDocumentTemplateRow): void {
    const selectedKeys = msg.documentSelectedKeys ?? [];

    this.patchDocumentMessage(msg, {
      documentSelectedKeys: selectedKeys.includes(row.key)
        ? selectedKeys.filter(key => key !== row.key)
        : [...selectedKeys, row.key]
    });
  }

  toggleAllDocumentTemplates(msg: ChatbotMessage): void {
    this.patchDocumentMessage(msg, {
      documentSelectedKeys: this.areAllDocumentTemplatesSelected(msg)
        ? []
        : this.getDocumentTemplates(msg).map(row => row.key)
    });
  }

  isDocumentTemplateDragging(msg: ChatbotMessage, row: ChatbotDocumentTemplateRow): boolean {
    return this.documentDragMessageId === msg.id && this.documentDragKeys.includes(row.key);
  }

  isDocumentTemplateDropBefore(msg: ChatbotMessage, index: number): boolean {
    return this.documentDragMessageId === msg.id && this.documentDropIndex === index;
  }

  isDocumentTemplateDropAfter(msg: ChatbotMessage, index: number): boolean {
    const rowCount = this.getDocumentTemplates(msg).length;
    return this.documentDragMessageId === msg.id && this.documentDropIndex === rowCount && index === rowCount - 1;
  }

  /** Pointer based drag so reordering also works on touch devices (tablets).
   *  Only the Document Template name cell is a drag handle — selection lives
   *  in its own column and is never touched here. */
  startDocumentTemplateDrag(
    event: PointerEvent,
    msg: ChatbotMessage,
    row: ChatbotDocumentTemplateRow,
    index: number
  ): void {
    if (!msg.id || (event.button ?? 0) !== 0) return;

    const messageId = msg.id;
    const rowElement = event.currentTarget as HTMLElement;
    const pointerId = event.pointerId;
    const startY = event.clientY;
    const selectedKeys = msg.documentSelectedKeys ?? [];
    // Dragging never changes the selection: a checked row moves the whole checked group,
    // an unchecked row moves on its own.
    const draggedKeys = selectedKeys.includes(row.key) ? [...selectedKeys] : [row.key];
    let isArmed = false;

    const armDrag = () => {
      if (isArmed) return;

      isArmed = true;
      this.documentDragMessageId = messageId;
      this.documentDragKeys = draggedKeys;
      this.documentDropIndex = index;

      try {
        rowElement.setPointerCapture(pointerId);
      } catch {
        /* pointer capture is best effort */
      }
    };

    // On touch a long press arms the drag so vertical scrolling still works.
    const longPressTimer = event.pointerType === 'touch' ? window.setTimeout(armDrag, 260) : null;

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (!isArmed) {
        if (moveEvent.pointerType === 'touch') return;
        if (Math.abs(moveEvent.clientY - startY) < 5) return;
        armDrag();
      }

      moveEvent.preventDefault();

      const elementAtPoint = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY) as HTMLElement | null;
      const targetRow = elementAtPoint?.closest('[data-doc-row]') as HTMLElement | null;
      if (!targetRow) return;

      const bounds = targetRow.getBoundingClientRect();
      const targetIndex = Number(targetRow.getAttribute('data-doc-row'));
      this.documentDropIndex = moveEvent.clientY > bounds.top + bounds.height / 2 ? targetIndex + 1 : targetIndex;
    };

    const onPointerUp = () => {
      if (longPressTimer) window.clearTimeout(longPressTimer);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      if (isArmed) this.moveDocumentTemplatesTo(messageId, draggedKeys, this.documentDropIndex);

      this.documentDragMessageId = null;
      this.documentDragKeys = [];
      this.documentDropIndex = null;
    };

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }

  moveDocumentTemplates(msg: ChatbotMessage, direction: -1 | 1): void {
    const rows = this.getDocumentTemplates(msg);
    const selectedKeys = msg.documentSelectedKeys ?? [];
    if (!msg.id || !selectedKeys.length) return;

    const selectedIndexes = rows
      .map((row, index) => (selectedKeys.includes(row.key) ? index : -1))
      .filter(index => index >= 0);
    const dropIndex = direction < 0 ? Math.min(...selectedIndexes) - 1 : Math.max(...selectedIndexes) + 2;
    if (dropIndex < 0 || dropIndex > rows.length) return;

    this.moveDocumentTemplatesTo(msg.id, selectedKeys, dropIndex);
  }

  /** Places the fixed Schedule PE / Site Plan row directly below the bottom-most
   *  checked row. Each has at most one live instance — pressing it again with a
   *  different selection relocates that same row rather than duplicating it.
   *  No-op when nothing is checked, or when the backend sent "empty" for that slot. */
  insertDocumentTemplate(msg: ChatbotMessage, kind: 'schedulePe' | 'sitePlan'): void {
    const reservedRow = kind === 'schedulePe' ? msg.documentSchedulePeRow : msg.documentSitePlanRow;
    if (!msg.id || !reservedRow || reservedRow.name === 'empty') return;

    const selectedKeys = msg.documentSelectedKeys ?? [];
    if (!selectedKeys.length) return;

    const rows = this.getDocumentTemplates(msg).filter(row => row.key !== reservedRow.key);
    const selectedIndexes = rows
      .map((row, index) => (selectedKeys.includes(row.key) ? index : -1))
      .filter(index => index >= 0);
    if (!selectedIndexes.length) return;

    const dropIndex = Math.max(...selectedIndexes) + 1;

    this.patchDocumentMessage(msg, {
      documentTemplates: [...rows.slice(0, dropIndex), reservedRow, ...rows.slice(dropIndex)]
    });
  }

  /** Opens the file picker for "+ Insert Custom Document". No-op when nothing is checked —
   *  same as the Schedule PE/Site Plan buttons — since there'd be nowhere to place the row. */
  triggerCustomDocumentPicker(msg: ChatbotMessage): void {
    if (!msg.id || !(msg.documentSelectedKeys ?? []).length) return;

    this.customDocumentTargetMessageId = msg.id;
    this.customDocumentInput?.nativeElement.click();
  }

  /** Sends the picked file to check_aps_info_document and, only on a successful response,
   *  inserts the returned document as a new row directly below the bottom-most checked row —
   *  same placement rule as insertDocumentTemplate(). The row is left unchecked (viewable,
   *  not selected) and is never added to filtered_document_names. On failure this does
   *  nothing — no error is surfaced to the user. */
  async onCustomDocumentFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    const messageId = this.customDocumentTargetMessageId;
    this.customDocumentTargetMessageId = null;
    input.value = '';

    if (!file || !messageId) return;

    const message = this.messages.find(candidate => candidate.id === messageId);
    if (!message) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    if (!isPdf && !isDocx) return;

    try {
      const base64 = await this.fileToBase64(file);
      const data = await this.postJson(`${environment.baseURLChatbot}check_aps_info_document`, {
        aps_data_input: '',
        pdf_base64: base64,
        pdf_file_name: file.name,
        ids_for_local_storage: this.getIdsForLocalStorage(),
        sales_role: this.currentRole
      });

      const [outputApsList, updatedIdsForLocalStorage] = Array.isArray(data) ? data : [data, null];
      this.storeInitializationData(updatedIdsForLocalStorage);

      const [documentName, documentUrl] = Array.isArray(outputApsList) ? outputApsList : [];
      if (!documentName || !documentUrl) return;

      this.insertCustomDocumentRow(message, String(documentName), String(documentUrl));
    } catch (error) {
      console.error('check_aps_info_document failed', error);
    }
  }

  private insertCustomDocumentRow(msg: ChatbotMessage, name: string, url: string): void {
    const selectedKeys = msg.documentSelectedKeys ?? [];
    if (!selectedKeys.length) return;

    const rows = this.getDocumentTemplates(msg);
    const selectedIndexes = rows
      .map((row, index) => (selectedKeys.includes(row.key) ? index : -1))
      .filter(index => index >= 0);
    if (!selectedIndexes.length) return;

    const dropIndex = Math.max(...selectedIndexes) + 1;
    const newRow: ChatbotDocumentTemplateRow = { key: this.#customDocumentKeySeq--, name, url };

    this.patchDocumentMessage(msg, {
      documentTemplates: [...rows.slice(0, dropIndex), newRow, ...rows.slice(dropIndex)]
    });
  }

  /* ── Document viewer (inline preview for the table's View column) ── */
  viewingDocument: ChatbotDocumentViewer | null = null;
  // Computed once when the viewer opens and reused as-is — never recompute this in a
  // getter/template expression. bypassSecurityTrustResourceUrl() returns a new wrapper
  // object on every call, and Angular's [src] binding treats a new object as a changed
  // value even when the URL string is identical, forcing the iframe to reload on every
  // change-detection tick (this component runs several recurring timers).
  documentViewerUrl: SafeResourceUrl | null = null;
  // Object URL for the fetched PDF blob backing documentViewerUrl. Tracked separately so
  // it can be revoked (freeing the underlying memory) on close/replace/destroy.
  #documentBlobUrl: string | null = null;

  private getDocumentKind(url: string): 'pdf' | 'docx' | 'unsupported' {
    const cleanUrl = url.split(/[?#]/)[0].toLowerCase();
    if (cleanUrl.endsWith('.pdf')) return 'pdf';
    if (cleanUrl.endsWith('.docx') || cleanUrl.endsWith('.doc')) return 'docx';
    return 'unsupported';
  }

  isDocumentTemplateViewable(row: ChatbotDocumentTemplateRow): boolean {
    return Boolean(row.url) && this.getDocumentKind(row.url!) !== 'unsupported';
  }

  viewDocumentTemplate(msg: ChatbotMessage, row: ChatbotDocumentTemplateRow): void {
    if (!row.url) return;
    void this.openDocumentViewer(row.name, row.url);
  }

  isPdfMessageViewable(msg: ChatbotMessage): boolean {
    return Boolean(msg.pdfUrl) && this.getDocumentKind(msg.pdfUrl!) !== 'unsupported';
  }

  viewPdfMessage(msg: ChatbotMessage): void {
    if (!msg.pdfUrl || !msg.pdfName) return;
    void this.openDocumentViewer(msg.pdfName, msg.pdfUrl);
  }

  private async openDocumentViewer(name: string, url: string): Promise<void> {
    const kind = this.getDocumentKind(url);
    if (kind === 'unsupported') return;

    this.viewingDocument = { name, url, kind };
    this.revokeDocumentBlobUrl();

    if (kind === 'docx') {
      // Office Online has to fetch this URL itself from its own servers, so it needs the
      // real S3 link — a local blob: URL is scoped to this tab and unreachable to it.
      const rawUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      this.documentViewerUrl = this.#sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
      return;
    }

    // Some presigned S3 URLs carry response-content-disposition=attachment, which forces
    // a download when the iframe navigates to it directly. fetch() ignores that header
    // entirely (it only reads response bytes), so we pull the PDF ourselves and hand the
    // iframe a local blob: URL instead, which has no HTTP headers for the disposition to
    // act on.
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);

      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      this.#documentBlobUrl = URL.createObjectURL(pdfBlob);

      // 'zoom=50' sets the initial zoom level; 'toolbar=0' hides Chrome's built-in PDF
      // toolbar entirely (there is no way to hide just its Summarize/Gemini button on
      // its own) — both are standard PDF open-parameters read from the URL fragment.
      const rawUrl = `${this.#documentBlobUrl}#toolbar=0&zoom=50`;
      this.documentViewerUrl = this.#sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
    } catch (error) {
      console.error('Failed to load PDF preview', error);
      this.viewingDocument = null;
      this.documentViewerUrl = null;
    }
  }

  private revokeDocumentBlobUrl(): void {
    if (!this.#documentBlobUrl) return;

    URL.revokeObjectURL(this.#documentBlobUrl);
    this.#documentBlobUrl = null;
  }

  closeDocumentViewer(): void {
    this.revokeDocumentBlobUrl();
    this.viewingDocument = null;
    this.documentViewerUrl = null;
  }

  canGenerateContract(msg: ChatbotMessage): boolean {
    return (msg.documentSelectedKeys ?? []).length > 0;
  }

  generateContract(msg: ChatbotMessage): void {
    const selectedKeys = msg.documentSelectedKeys ?? [];
    if (!selectedKeys.length) return;

    const selectedNames = this.getDocumentTemplates(msg)
      .filter(row => selectedKeys.includes(row.key))
      .map(row => row.name);

    this.appendFilteredDocumentNames(selectedNames);
    void this.sendMessage('Generate Contract for this deal');
  }

  private appendFilteredDocumentNames(names: string[]): void {
    if (!isPlatformBrowser(this.#platformId)) return;

    const idsForLocalStorage = this.getIdsForLocalStorage() ?? {};
    const existingNames = Array.isArray(idsForLocalStorage.filtered_document_names)
      ? idsForLocalStorage.filtered_document_names
      : [];

    idsForLocalStorage.filtered_document_names = [...existingNames, ...names];
    localStorage.setItem('ids_for_local_storage', JSON.stringify(idsForLocalStorage));
  }

  private moveDocumentTemplatesTo(messageId: number, keys: number[], dropIndex: number | null): void {
    if (dropIndex === null) return;

    const message = this.messages.find(candidate => candidate.id === messageId);
    if (!message) return;

    const rows = message.documentTemplates ?? [];
    const movedRows = rows.filter(row => keys.includes(row.key));
    const remainingRows = rows.filter(row => !keys.includes(row.key));
    const insertAt = rows.slice(0, dropIndex).filter(row => !keys.includes(row.key)).length;

    this.patchDocumentMessage(message, {
      documentTemplates: [...remainingRows.slice(0, insertAt), ...movedRows, ...remainingRows.slice(insertAt)]
    });
  }

  private patchDocumentMessage(msg: ChatbotMessage, patch: Partial<ChatbotMessage>): void {
    if (!msg.id) return;

    this.messages = this.messages.map(message => (message.id === msg.id ? { ...message, ...patch } : message));
    this.persistMessagesToStorage();
  }

  /* ── Attribute dropdowns (AttributeArrayTable) ── */
  readonly attributeFields: { key: ChatbotAttributeKey; label: string; placeholder: string }[] = [
    { key: 'location', label: 'Location', placeholder: 'Select Location' },
    { key: 'attribute1', label: 'Attribute Array 1', placeholder: 'Select Attribute 1' },
    { key: 'attribute2', label: 'Attribute Array 2', placeholder: 'Select Attribute 2' }
  ];
  // UI-only, not persisted — only one dropdown panel is open at a time.
  attributeOpen: { msgId: number; key: ChatbotAttributeKey } | null = null;
  attributeQuery = '';

  @HostListener('document:click')
  onAttributeOutsideClick(): void {
    if (this.attributeOpen) this.closeAttributeDropdown();
  }

  @HostListener('document:keydown.escape')
  onAttributeEscape(): void {
    if (this.attributeOpen) this.closeAttributeDropdown();
  }

  getAttributeOptions(msg: ChatbotMessage, key: ChatbotAttributeKey): string[] {
    return msg.attributeArrays?.[key] ?? [];
  }

  getFilteredAttributeOptions(msg: ChatbotMessage, key: ChatbotAttributeKey): string[] {
    const query = this.attributeQuery.trim().toLowerCase();
    return this.getAttributeOptions(msg, key).filter(option => option.toLowerCase().includes(query));
  }

  getAttributeValue(msg: ChatbotMessage, key: ChatbotAttributeKey): string | null {
    return msg.attributeSelection?.[key] ?? null;
  }

  isAttributeDisabled(msg: ChatbotMessage, key: ChatbotAttributeKey): boolean {
    return this.getAttributeOptions(msg, key).length === 0;
  }

  isAttributeOpen(msg: ChatbotMessage, key: ChatbotAttributeKey): boolean {
    return this.attributeOpen?.msgId === msg.id && this.attributeOpen?.key === key;
  }

  toggleAttributeDropdown(event: Event, msg: ChatbotMessage, key: ChatbotAttributeKey): void {
    event.stopPropagation();
    if (!msg.id || this.isAttributeDisabled(msg, key)) return;

    const wasOpen = this.isAttributeOpen(msg, key);
    this.attributeQuery = '';
    this.attributeOpen = wasOpen ? null : { msgId: msg.id, key };
  }

  pickAttribute(event: Event, msg: ChatbotMessage, key: ChatbotAttributeKey, option: string): void {
    event.stopPropagation();
    this.patchDocumentMessage(msg, {
      attributeSelection: { ...this.emptyAttributeSelection(), ...msg.attributeSelection, [key]: option }
    });
    this.closeAttributeDropdown();
  }

  clearAttribute(event: Event, msg: ChatbotMessage, key: ChatbotAttributeKey): void {
    event.stopPropagation();
    this.patchDocumentMessage(msg, {
      attributeSelection: { ...this.emptyAttributeSelection(), ...msg.attributeSelection, [key]: null }
    });
  }

  resetAttributes(msg: ChatbotMessage): void {
    this.patchDocumentMessage(msg, { attributeSelection: this.emptyAttributeSelection() });
    this.closeAttributeDropdown();
  }

  /** Always enabled — any number of values (including none) is a valid selection.
   *  Unselected values are sent as "". */
  confirmAttributes(msg: ChatbotMessage): void {
    this.closeAttributeDropdown();

    const valueOf = (key: ChatbotAttributeKey) => this.getAttributeValue(msg, key) || '""';
    const filledText = String(msg.attributePromptTemplate ?? '')
      .replace('{Subcategory1}', valueOf('attribute1'))
      .replace('{subcategory2}', valueOf('attribute2'))
      .replace('{Location}', valueOf('location'));

    void this.sendMessage(filledText);
  }

  closeAttributeDropdown(): void {
    this.attributeOpen = null;
    this.attributeQuery = '';
  }

  private emptyAttributeSelection(): ChatbotAttributeSelection {
    return { location: null, attribute1: null, attribute2: null };
  }

  pageArray(n: number): number[] {
    return Array.from({ length: n }, (_, i) => i);
  }

  private getUserScopedKey(suffix: string): string | null {
    if (!isPlatformBrowser(this.#platformId)) return null;

    const userId = this.#localStorageService.getUserIdFromStorage();
    return userId ? `${userId}-${suffix}` : null;
  }

  private hasChatbotBeenInitialized(): boolean {
    const key = this.getUserScopedKey(this.#chatbotInitializedKey);
    if (!key) return false;

    return localStorage.getItem(key) === '1';
  }

  private markChatbotInitialized(): void {
    const key = this.getUserScopedKey(this.#chatbotInitializedKey);
    if (!key) return;

    localStorage.setItem(key, '1');
  }

  private persistMessagesToStorage(): void {
    const key = this.getUserScopedKey(this.#chatbotMessagesKey);
    if (!key) return;

    localStorage.setItem(key, JSON.stringify(this.getPersistableMessages(this.messages)));
  }

  private restoreMessagesFromStorage(): ChatbotMessage[] {
    const key = this.getUserScopedKey(this.#chatbotMessagesKey);
    if (!key) return [];

    const storedMessages = localStorage.getItem(key);
    if (!storedMessages) return [];

    try {
      const parsedMessages = JSON.parse(storedMessages);
      const messages = Array.isArray(parsedMessages) ? this.getPersistableMessages(parsedMessages) : [];
      this.#msgSeq = messages.reduce((max, message) => {
        const id = typeof message.id === 'number' ? message.id : Number(message.id);
        return Number.isFinite(id) ? Math.max(max, id) : max;
      }, 0);
      return messages;
    } catch {
      return [];
    }
  }

  private getPersistableMessages(messages: ChatbotMessage[]): ChatbotMessage[] {
    if (!Array.isArray(messages) || messages.length === 0) {
      return [];
    }

    const clientIndices = messages.reduce<number[]>((indices, message, index) => {
      if (message?.sender === 'Client') {
        indices.push(index);
      }
      return indices;
    }, []);

    if (clientIndices.length <= this.PERSISTED_PAIR_LIMIT) {
      return messages;
    }

    const startIndex = clientIndices[clientIndices.length - this.PERSISTED_PAIR_LIMIT];
    return messages.slice(startIndex);
  }
}
